import { computed, type Signal } from '@preact/signals-react'
import type { ActiveOmniMate } from '../../localMates/activeOmniMate/activeOmniMate.types'
import type { ControllerMates } from '../../localMates/controllerMates/controllerMates.types'
import type { RoomState } from '../../room/room.types'
import type { GameRegistry } from '../../catalog/gameRegistry/gameRegistry.types'
import type { Mode } from '../../mode/mode/mode.types'
import type { VideoDep } from '../../video/video/video.types'
import type { Bot } from '../../bot/bot/bot.types'
import type { LocalMates } from '../../localMates/localMates/localMates.types'
import type { MateListVM, MateListVMPlayer, MateListVMMate } from './mateListVM.types'
import { hostTermInSentence } from '../../utils/hostTerm'

/**
 * Get bgio player index from game-specific player ID.
 *
 * - When `playerIds` exists (game-defined player IDs), use it to map the ID -> seat index.
 * - Otherwise, assume the ID is a numeric string representing the bgio seat index.
 */
function getBgioIndexFromPlayerIds(playerIds: string[] | undefined, gamePlayerId: string): number | null {
  if (playerIds) {
    const index = playerIds.indexOf(gamePlayerId)
    return index >= 0 ? index : null
  }
  const parsed = parseInt(gamePlayerId)
  return isNaN(parsed) ? null : parsed
}

function dedupeById<T extends { id: string }>(list: T[]): T[] {
  const seen = new Set<string>()
  const out: T[] = []
  for (const item of list) {
    if (!item?.id) continue
    if (seen.has(item.id)) continue
    seen.add(item.id)
    out.push(item)
  }
  return out
}

function dedupePlayerMates<T extends { id: string; mates: Array<{ id: string }> }>(list: T[]): T[] {
  return list.map((player) => ({
    ...player,
    mates: dedupeById(player.mates as any),
  }))
}

function findSlotIndexForMateId(playerToMateIds: string[][] | undefined, mateId: string | undefined): number | null {
  if (!mateId) return null
  if (!playerToMateIds) return null
  for (let i = 0; i < playerToMateIds.length; i++) {
    const group = playerToMateIds[i]
    if (!Array.isArray(group)) continue
    if (group.includes(mateId)) return i
  }
  return null
}

export type MateListVMDeps = {
  activeOmniMate: ActiveOmniMate
  roomState: RoomState
  gameRegistry: GameRegistry
  controllerMates: ControllerMates
  mode: Mode
  video: VideoDep
  localMates: LocalMates
  bot: Bot
  /** This device's own clientId - controller mates share it (see sharedScreenPregameRoster). */
  clientId: string
  /** See wiki/product/creator-playtesting.md's "Platform diagnostics" - widens the Omni-mode player-count picker beyond the active game's own min/max players. */
  isPlatformDeveloper: Signal<boolean>
  /** Whether this client's user owns the room; only the owner designates hosts. */
  isOwner: Signal<boolean>
  setHost: (mateId: string, isHost: boolean) => Promise<void>
}

export function makeMateListVMDep(deps: MateListVMDeps): MateListVM {
  const { activeOmniMate, roomState, gameRegistry, controllerMates, mode, video, clientId, localMates, bot, isPlatformDeveloper, isOwner, setHost } = deps

  /**
   * Host status, its badge, and whether this client (the owner) may change it
   * for a mate. The badge uses the room's name for hosts and shows only in a
   * managed room, where hosts are the ones who run games.
   */
  function getHostFields(mateId: string, mate: { isAI?: boolean } | undefined) {
    const isHost = roomState.hostMateIds.value.includes(mateId)
    const term = roomState.hostTerm.value
    return {
      isHost,
      hostBadge: isHost && roomState.managed.value ? term : undefined,
      canToggleHost: isOwner.value && Boolean(mate) && !mate?.isAI,
      hostToggleLabel: `${isHost ? 'Remove' : 'Make'} ${hostTermInSentence(term)}`,
    }
  }

  /**
   * Omni mode fields for a mate: whether this client is acting as it, and
   * whether clicking its tile may switch to it. Any connected client may act
   * as any Omni mate in the room (see activeOmniMate's localOmniMateIds).
   */
  function getOmniFields(mateId: string) {
    const isActingAs = activeOmniMate.activeLocalOmniMateId.value === mateId
    const omniIds = activeOmniMate.localOmniMateIds.value ?? []
    return { isActingAs, canActAs: !isActingAs && omniIds.includes(mateId) }
  }

  // Video is scoped to the device (clientId), not the mate - see ../../video/AGENTS.md -
  // except a fake Omni mate never gets video even though it shares its owning client's
  // clientId with that client's own real (video-eligible) mate: without this check, every
  // Omni mate would otherwise inherit that one real track (see video.ts's role, which
  // keeps publishing keyed on the real mate alone).
  function getVideoFields(mate: { clientId?: string; isOmniMate?: boolean } | undefined) {
    if (mate?.isOmniMate) {
      return { videoTrack: undefined, audioTrack: undefined, isLocalParticipant: undefined, videoControls: undefined }
    }
    const clientId = mate?.clientId
    const participant = clientId ? video.participants.value.get(clientId) : undefined
    const isLocalParticipant = participant?.isLocal
    return {
      videoTrack: participant?.videoTrack,
      audioTrack: participant?.audioTrack,
      isLocalParticipant,
      // Only the local mate's own tile gets mute controls - see MateListVMVideoControls.
      // Device selection is not per-mate; see mateList/controls/DeviceSelector.
      videoControls: isLocalParticipant
        ? {
            cameraOn: video.localState.value.cameraOn,
            micOn: video.localState.value.micOn,
            setCameraEnabled: video.setCameraEnabled,
            setMicEnabled: video.setMicEnabled,
          }
        : undefined,
    }
  }

  const isLocalMode = computed(() => mode.isLocalMode())
  const isOmniMode = computed(() => mode.isOmniMode())
  const isSharedScreen = computed(() => Boolean(mode.computedMode.value.sharedScreen))

  // A room with any Omni mate present has a real mate somewhere whose tile may be
  // redundant to show: solo (no partner), not in gameless mode, and videoconferencing
  // off, there is nothing useful on it - no other mate to see, no video to show - so
  // it is hidden entirely. With a partner present, in gameless mode, or with
  // videoconferencing on (there may be live video to show/mute even solo - see
  // video.ts's role, which publishes for a solo real mate whenever videoEnabled is
  // on), it is shown normally (see computedObservers below). Never hides anything
  // when Omni mode isn't in play at all.
  const hasAnyOmniMate = computed(() => (roomState.mates.value || []).some((m: any) => m.isOmniMate))
  const realMateCount = computed(() => (roomState.mates.value || []).filter((m: any) => !m.isOmniMate).length)
  const isGamelessOn = computed(() => Boolean(roomState.gameless?.value))
  const shouldHideSoloRealMate = computed(() => (
    hasAnyOmniMate.value && realMateCount.value <= 1 && !isGamelessOn.value && !roomState.videoEnabled.value
  ))

  const activeGame = computed(() => roomState.activeGame.value ?? null)
  const hasActiveGame = computed(() => Boolean(activeGame.value))
  // Cooperative system challenges have one shared solving role, even when several
  // mates participate. Hiding seat labels avoids presenting them as opposing sides.
  const hidePlayerLabels = computed(() => {
    const configuration = activeGame.value?.challengeConfiguration
    return configuration?.challengeSource === 'game'
      && configuration.gameChallengeArrangement === 'cooperative'
  })
  const gameId = computed(() => activeGame.value?.gameId ?? roomState.gameSetup.value?.gameId ?? null)

  const PLATFORM_DEVELOPER_PLAYER_COUNT_OPTIONS = [1, 2, 3, 4, 5]

  // How many players Omni mode can be set up for. Platform developers (see
  // wiki/product/creator-playtesting.md) get a fixed 1-5 regardless of what
  // the active game declares, to exercise it outside its own supported range;
  // everyone else only sees counts the game itself actually supports.
  const playerCountOptions = computed<number[]>(() => {
    if (isPlatformDeveloper.value) return PLATFORM_DEVELOPER_PLAYER_COUNT_OPTIONS
    const id = gameId.value
    const metadata = id ? gameRegistry.getGameMetadata?.(id) : null
    if (!metadata) return []
    const options: number[] = []
    for (let n = metadata.minPlayers; n <= metadata.maxPlayers; n++) options.push(n)
    return options
  })

  // In a managed room this client's host Omni mate coordinates rather than
  // plays, so it is not one of the players the picker counts.
  const omniHostCount = computed(() => {
    if (!roomState.managed.value) return 0
    return localMates.localMates.value.filter((m: any) => m.isOmniMate && roomState.hostMateIds.value.includes(m.mateId)).length
  })

  // How many of this client's Omni mates play (meaningless - and never shown -
  // outside Omni mode, where this client has none).
  const omniPlayerCount = computed(() => (
    localMates.localMates.value.filter((m: any) => m.isOmniMate).length - omniHostCount.value
  ))

  function setPlayerCount(count: number): void {
    localMates.setOmniMateCount(count + omniHostCount.value)
  }

  const supportsBots = computed(() => {
    const id = gameId.value
    return id ? gameRegistry.supportsBots(id) : false
  })

  const showAddBot = computed(() => {
    const botCount = (roomState.mates.value || []).filter((mate: any) => mate.isAI).length
    if (botCount >= 1) return false
    // If there is no active game (e.g. Catalog screen, setup screen), always allow adding bots
    if (!hasActiveGame.value) {
      return true
    }
    // If there is an active game, only show it if the game supports bots
    return supportsBots.value
  })

  const actingAsSlotIndex = computed<number | null>(() => {
    const game = activeGame.value
    return findSlotIndexForMateId(game?.playerToMateIds, activeOmniMate.activeLocalOmniMateId.value)
  })

  const currentPlayerId = computed<string | undefined>(() => {
    const idx = actingAsSlotIndex.value
    if (idx === null) return undefined
    return roomState.getPlayerIdForSlot(idx) ?? String(idx)
  })

  const activePlayerId = computed<string | undefined>(() => {
    const ctx = activeGame.value?.ctx
    if (ctx?.currentPlayer === undefined) return undefined

    // Boardgame.io races mark every contestant active at once. There is no
    // single turn owner in that state, so use neutral chrome instead.
    if (ctx.activePlayers && Object.keys(ctx.activePlayers).length > 1) return undefined

    const bgioPlayerIndex = parseInt(ctx.currentPlayer)
    if (isNaN(bgioPlayerIndex)) return undefined
    return roomState.getPlayerIdForSlot(bgioPlayerIndex)
  })

  const matesByMateId = computed(() => {
    const map = new Map<string, any>()
    for (const m of (roomState.mates.value || []) as any[]) {
      const mateId = (m as any).mateId
      if (typeof mateId === 'string') {
        map.set(mateId, m)
      }
    }
    return map
  })

  // The move generator is shared by the room, so only the bot occupying the
  // active player slot can be shown as thinking.
  const thinkingBotMateId = computed(() => {
    const game = activeGame.value
    if (!game || game.ctx?.gameover || !bot.isProcessing.value) return undefined
    const playerIndex = Number(game.ctx?.currentPlayer)
    if (!Number.isInteger(playerIndex)) return undefined
    const mates = game.playerToMateIds[playerIndex]
    if (mates?.length !== 1) return undefined
    const mate = matesByMateId.value.get(mates[0])
    return mate?.isAI ? mate.mateId : undefined
  })

  const ourMateId = computed<string | undefined>(() => {
    // Shared-screen has multiple simultaneous local mates on this device; no single
    // "our mate" to highlight - each mate's own tile is handled separately there.
    if (isSharedScreen.value) return undefined
    // Omni mode adds its fake mates alongside this client's own real mate rather than
    // replacing it (see localMates.ts), which stays first in the list - "our mate" keeps
    // meaning this client's own identity, kept separate from whichever persona it's
    // currently playing as (see currentPlayerId/PlayingAsIndicator for that).
    return roomState.localMates.value.find((m: any) => !m.isOmniMate)?.mateId
  })

  const computedPlayers = computed<MateListVMPlayer[]>(() => {
    const game = activeGame.value
    if (!game?.playerToMateIds) return []

    const playerIdConfig = gameRegistry.getGameMetadata?.(game.gameId)?.playerIds
    const configuredPlayerIds = Array.isArray(playerIdConfig)
      ? playerIdConfig
      : playerIdConfig?.[game.playerToMateIds.length]
    const playerMap = new Map<string, MateListVMPlayer>()
    const matesById = matesByMateId.value

    game.playerToMateIds.forEach((group: string[], groupIndex: number) => {
      if (!Array.isArray(group)) return

      const gamePlayerId = roomState.getPlayerIdForSlot(groupIndex) ?? String(groupIndex)
      if (!playerMap.has(gamePlayerId)) {
        playerMap.set(gamePlayerId, {
          id: gamePlayerId,
          displayName: configuredPlayerIds?.[groupIndex] ?? gamePlayerId,
          mates: [],
        })
      }

      const player = playerMap.get(gamePlayerId)!

      group.forEach((mateId: string) => {
        const mate = matesById.get(mateId)
        player.mates.push({
          id: mateId,
          name: mate?.name || mateId,
          ...getVideoFields(mate),
          thumbnailUrl: mate?.thumbnailUrl,
          playerId: gamePlayerId,
          isBot: Boolean(mate?.isAI),
          canDismiss: Boolean(mate?.isAI) && localMates.localMateIds.value.has(mateId),
          isThinking: thinkingBotMateId.value === mateId,
        })
      })
    })

    return Array.from(playerMap.values())
  })

  const computedObservers = computed<MateListVMMate[]>(() => {
    const game = activeGame.value
    const roomMates = (roomState.mates.value || []).filter((m: any) => (
      m.isOmniMate || !shouldHideSoloRealMate.value
    ))

    if (!game) {
      // No active game: all mates are observers.
      return roomMates.map((m: any) => ({
        id: m.mateId,
        name: m.name || '',
        ...getVideoFields(m),
        thumbnailUrl: m.thumbnailUrl,
        isBot: Boolean(m.isAI),
        canDismiss: Boolean(m.isAI) && localMates.localMateIds.value.has(m.mateId),
        isThinking: thinkingBotMateId.value === m.mateId,
      }))
    }

    // Mates not in any player group.
    const inGameMateIds = new Set<string>()
    if (game.playerToMateIds) {
      for (const group of game.playerToMateIds) {
        if (!Array.isArray(group)) continue
        for (const id of group) {
          if (typeof id === 'string') inGameMateIds.add(id)
        }
      }
    }

    return roomMates
      .filter((m: any) => !inGameMateIds.has(m.mateId))
      .map((m: any) => ({
        id: m.mateId,
        name: m.name || '',
        isObserver: true,
        ...getVideoFields(m),
        thumbnailUrl: m.thumbnailUrl,
        isThinking: thinkingBotMateId.value === m.mateId,
      }))
  })

  const sharedScreenPregameRoster = computed<MateListVMMate[] | null>(() => {
    if (!isSharedScreen.value) return null
    if (hasActiveGame.value) return null

    // Networked shared-screen: show roster mates from the room (includes other clients).
    if (!isLocalMode.value && roomState.mates.value.length > 0) {
      const seen = new Set<string>()
      return roomState.mates.value
        .filter((m) => m.name && m.name.trim().length > 0)
        .map((m: any) => ({
          id: String(m.mateId || `${m.clientId}:${m.controllerId || 'self'}`),
          name: String(m.name || ''),
          thumbnailUrl: m.thumbnailUrl,
          ...getVideoFields(m),
        }))
        .filter((p: any) => {
          if (!p?.id) return false
          if (seen.has(p.id)) return false
          seen.add(p.id)
          return true
        })
    }

    // Local shared-screen (or not yet synced): show controller mates only.
    // They don't carry a clientId of their own - they all share this device's
    // clientId, so video fields (only ever populated for the 'solo' single-controller
    // case; 'shared' multi-controller devices never join Daily in phase 1) key off that.
    const seen = new Set<string>()
    return controllerMates.mates.value
      .filter((m) => m.name && m.name.trim().length > 0)
      .map((m) => ({
        id: m.id,
        name: m.name,
        thumbnailUrl: m.thumbnailUrl,
        ...getVideoFields({ clientId }),
      }))
      .filter((p: any) => {
        if (!p?.id) return false
        if (seen.has(p.id)) return false
        seen.add(p.id)
        return true
      })
  })

  const decorateMates = computed(() => {
    const matesById = matesByMateId.value

    const decoratePlayers = (list: MateListVMPlayer[]): MateListVMPlayer[] => {
      return list.map((player) => ({
        ...player,
        mates: player.mates.map((p) => {
          const mate = matesById.get(p.id)
          const type = (mate as any)?.type
          const clientId = (mate as any)?.clientId

          // For debugging: show worker mate id + seat if we have it.
          const debugLineParts: string[] = []
          if (clientId) debugLineParts.push(`client=${clientId}`)
          debugLineParts.push(`worker=${p.id}`)
          if (typeof p.playerId === 'string') debugLineParts.push(`seat=${p.playerId}`)

          const videoFields = getVideoFields(mate)
          return {
            ...p,
            type,
            clientId,
            thumbnailUrl: (mate as any)?.thumbnailUrl ?? p.thumbnailUrl,
            videoTrack: videoFields.videoTrack ?? p.videoTrack,
            audioTrack: videoFields.audioTrack ?? p.audioTrack,
            isLocalParticipant: videoFields.isLocalParticipant ?? p.isLocalParticipant,
            videoControls: videoFields.videoControls ?? p.videoControls,
            debugLine: debugLineParts.join(' '),
            botOwnerId: (mate as any)?.botOwnerId,
            isOmniMate: Boolean((mate as any)?.isOmniMate),
            canDismiss: Boolean((mate as any)?.isAI) && localMates.localMateIds.value.has(p.id),
            isThinking: thinkingBotMateId.value === p.id,
            ...getHostFields(p.id, mate as any),
            ...getOmniFields(p.id),
          }
        }),
      }))
    }

    const decorateObservers = (list: MateListVMMate[]): MateListVMMate[] => {
      return list.map((p) => {
        const mate = matesById.get(p.id)
        const type = (mate as any)?.type
        const clientId = (mate as any)?.clientId
        const debugLineParts: string[] = []
        if (clientId) debugLineParts.push(`client=${clientId}`)
        debugLineParts.push(`worker=${p.id}`)
        const videoFields = getVideoFields(mate)
        return {
          ...p,
          type,
          clientId,
          thumbnailUrl: (mate as any)?.thumbnailUrl ?? p.thumbnailUrl,
          videoTrack: videoFields.videoTrack ?? p.videoTrack,
          audioTrack: videoFields.audioTrack ?? p.audioTrack,
          isLocalParticipant: videoFields.isLocalParticipant ?? p.isLocalParticipant,
          videoControls: videoFields.videoControls ?? p.videoControls,
          debugLine: debugLineParts.join(' '),
          botOwnerId: (mate as any)?.botOwnerId,
          isOmniMate: Boolean((mate as any)?.isOmniMate),
          canDismiss: Boolean((mate as any)?.isAI) && localMates.localMateIds.value.has(p.id),
          isThinking: thinkingBotMateId.value === p.id,
          ...getHostFields(p.id, mate as any),
          ...getOmniFields(p.id),
        }
      })
    }

    const roster = sharedScreenPregameRoster.value
    if (roster) {
      return {
        players: [] as MateListVMPlayer[],
        observers: dedupeById(decorateObservers(roster)),
      }
    }

    return {
      players: dedupePlayerMates(decoratePlayers(computedPlayers.value)),
      observers: dedupeById(decorateObservers(computedObservers.value)),
    }
  })

  const withBotNames = computed(() => {
    const basePlayers = decorateMates.value.players
    const baseObservers = decorateMates.value.observers

    // Build a stable mateId -> name map from all mates we plan to render.
    const nameById = new Map<string, string>()
    for (const p of basePlayers) {
      for (const m of p.mates) nameById.set(m.id, m.name)
    }
    for (const m of baseObservers) nameById.set(m.id, m.name)

    const decorateMate = (m: MateListVMMate): MateListVMMate => {
      if (!m.isBot || !m.botOwnerId) return m
      const ownerName = nameById.get(m.botOwnerId)
      return ownerName ? { ...m, botDisplayName: `${ownerName}'s bot` } : m
    }

    return {
      players: basePlayers.map((p) => ({ ...p, mates: p.mates.map(decorateMate) })),
      observers: baseObservers.map(decorateMate),
    }
  })

  const players = computed(() => withBotNames.value.players)

  // Not in shared-screen mode: put our own mate first among observers (this is also
  // how the pre-game roster is ordered, since it's rendered as "everyone is an
  // observer" before a game starts - see computedObservers above). When the room
  // has any Omni mate, Omni mates come first instead, ahead of every real mate
  // (including this client's own) - see omniObserverCount below, which lets the UI
  // draw a separator between the two groups. This is keyed off hasAnyOmniMate
  // (room-wide), not isOmniMode (this client's own toggle), so every connected
  // client sees the same ordering regardless of who turned Omni mode on.
  const observers = computed<MateListVMMate[]>(() => {
    const list = withBotNames.value.observers
    if (isSharedScreen.value) return list

    if (hasAnyOmniMate.value) {
      const omni = list.filter((m) => m.isOmniMate)
      const real = list.filter((m) => !m.isOmniMate)
      return omni.length > 0 && real.length > 0 ? [...omni, ...real] : list
    }

    const myId = ourMateId.value
    if (!myId) return list
    const myIndex = list.findIndex((m) => m.id === myId)
    if (myIndex <= 0) return list
    const reordered = [...list]
    const [mine] = reordered.splice(myIndex, 1)
    reordered.unshift(mine)
    return reordered
  })

  // How many of the leading `observers` entries are Omni mates - 0 unless the room
  // has any Omni mate and both groups (Omni mates and real mates) are actually
  // present. Room-wide (see observers above), so the separator renders identically
  // for every connected client.
  const omniObserverCount = computed(() => {
    if (!hasAnyOmniMate.value) return 0
    const list = observers.value
    const count = list.filter((m) => m.isOmniMate).length
    return count > 0 && count < list.length ? count : 0
  })

  // Display order (a permutation of indices into `players`) for showing our own player
  // group first when not in shared-screen mode. `players` itself stays index-stable,
  // since other logic (turn colors, player numbering) keys off its original slot index.
  const playerDisplayOrder = computed<number[]>(() => {
    const list = players.value
    const order = list.map((_, i) => i)
    if (isSharedScreen.value) return order
    const myId = ourMateId.value
    if (!myId) return order
    const myIndex = list.findIndex((p) => p.mates.some((m) => m.id === myId))
    if (myIndex <= 0) return order
    const reordered = [...order]
    const [mine] = reordered.splice(myIndex, 1)
    reordered.unshift(mine)
    return reordered
  })

  // ===== Omni-mode derived values (for UI controls) =====
  const currentPlayerIndex = computed<number | null>(() => {
    const id = currentPlayerId.value
    if (!id) return null
    const index = players.value.findIndex((p) => p.id === id)
    return index >= 0 ? index : null
  })

  const shouldUsePlayerColors = computed(() => {
    // Current-player color cues are an Omni-mode-only affordance (playing-as
    // switching) - room-wide, since any connected client may act as any Omni
    // mate, not just the client that turned Omni mode on.
    if (!hasAnyOmniMate.value) return false
    if (players.value.length !== 2) return false
    return currentPlayerIndex.value !== null
  })

  function setAutoMode(checked: boolean): void {
    activeOmniMate.setAutoSwitchLocalOmniMate(checked)
  }

  function selectPlayer(playerId: string): void {
    const game = activeGame.value
    const bgioIndex = getBgioIndexFromPlayerIds(game?.playerIds, playerId)
    if (bgioIndex === null) return
    const localIds = activeOmniMate.localOmniMateIds.value
    const matesInSlot = game?.playerToMateIds?.[bgioIndex] || null
    if (!localIds || localIds.length < 1) return
    if (!matesInSlot || matesInSlot.length < 1) return

    const localMateId = matesInSlot.find((id) => localIds.includes(id))
    if (!localMateId) return
    const idx = localIds.indexOf(localMateId)
    if (idx < 0) return
    activeOmniMate.setActiveLocalOmniMateIndex(idx)
  }

  /** Switches this client to acting as the given Omni mate, such as the observing host. */
  function actAs(mateId: string): void {
    const idx = activeOmniMate.localOmniMateIds.value?.indexOf(mateId) ?? -1
    if (idx < 0) return
    activeOmniMate.setActiveLocalOmniMateIndex(idx)
  }

  // Room-wide (not just this client's own isOmniMode toggle) - any connected
  // client may pick which Omni mate it's acting as.
  const isPlayerSelectionEnabled = computed(() => hasAnyOmniMate.value && !activeOmniMate.autoSwitchLocalOmniMate.value)

  return {
    players,
    playerDisplayOrder,
    observers,
    omniObserverCount,
    ourMateId,

    isSharedScreen,
    isOmniMode,
    hasAnyOmniMate,
    isLocalMode,
    hasActiveGame,
    hidePlayerLabels,

    autoMode: activeOmniMate.autoSwitchLocalOmniMate,
    activePlayerId,
    currentPlayerId,
    isPlayerSelectionEnabled,

    omniMode: {
      currentPlayerIndex,
      shouldUsePlayerColors,
    },

    supportsBots,
    showAddBot,
    gameId,
    playerCountOptions,
    omniPlayerCount,

    setAutoMode,
    selectPlayer,
    actAs,
    setPlayerCount,

    addAIPlayer: () => {
      localMates.addAIPlayer()
    },
    toggleHost: (mateId: string) => {
      const mate = matesByMateId.value.get(mateId)
      if (!getHostFields(mateId, mate as any).canToggleHost) return
      setHost(mateId, !roomState.hostMateIds.value.includes(mateId)).catch((err) => {
        console.error('[MateListVM] Failed to change host:', err)
      })
    },
    dismissBot: (mateId: string) => {
      const mate = matesByMateId.value.get(mateId)
      if (!mate?.isAI || !localMates.localMateIds.value.has(mateId)) return
      localMates.removeMate(mateId)
    },
  }
}
